/**
 * SkinSelector — Elegir skin.
 *
 * La versión anterior tenía siete presets de cinco colores planos y, sobre
 * todo, su onSkinChanged no estaba enchufado a nada: elegir una skin no
 * cambiaba nada en pantalla. Solo se abría escribiendo «skin» en la terminal.
 *
 * Ahora son cuarenta skins de verdad —textura de 64×64 en el formato estándar
 * de Minecraft— repartidas en siete categorías, con vista previa de frente y
 * de espaldas, y el cambio se ve en el acto en tercera persona (F5).
 */
import { SKINS, CATEGORIAS, porCategoria, buscarSkin } from '../player/skins/catalogo.js';
import { retrato } from '../player/skins/pintor.js';

const CLAVE = 'fedeworld_skin';

export class SkinSelector {
  constructor() {
    this.currentSkin = buscarSkin(localStorage.getItem(CLAVE));
    this.onSkinChanged = null;
    this._cat = 'todas';
    this._dorso = false;
    this._buildUI();
  }

  getCurrentSkin() { return this.currentSkin; }

  _buildUI() {
    this._overlay = document.createElement('div');
    this._overlay.id = 'skin-selector';
    this._overlay.style.cssText = `
      position:fixed; inset:0; background:rgba(0,0,0,0.82); z-index:915;
      display:none; justify-content:center; align-items:center;
      font-family:'Courier New',monospace;`;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:#2A2A2A; border:4px solid; border-color:#4A4A4A #1A1A1A #1A1A1A #4A4A4A;
      padding:20px 24px; width:min(900px,94vw); max-height:90vh; display:flex;
      flex-direction:column; gap:14px; color:#DDD;`;

    const titulo = document.createElement('div');
    titulo.textContent = `Elige tu skin — ${SKINS.length} disponibles`;
    titulo.style.cssText = 'font-size:22px;font-weight:bold;color:#FFD700;text-align:center;';
    panel.appendChild(titulo);

    /* --- cuerpo: previsualización a la izquierda, rejilla a la derecha --- */
    const cuerpo = document.createElement('div');
    cuerpo.style.cssText = 'display:flex; gap:18px; min-height:0; flex-wrap:wrap;';

    const lado = document.createElement('div');
    lado.style.cssText = 'width:180px; flex:0 0 auto; text-align:center;';
    this._preview = document.createElement('div');
    this._preview.style.cssText = `
      background:#1E1E1E; border:2px solid #444; padding:10px; min-height:210px;
      display:flex; align-items:center; justify-content:center;`;
    lado.appendChild(this._preview);

    this._nombre = document.createElement('div');
    this._nombre.style.cssText = 'font-size:16px;font-weight:bold;color:#FFF;margin-top:8px;';
    lado.appendChild(this._nombre);

    const girar = document.createElement('button');
    girar.textContent = 'Girar ⟳';
    girar.style.cssText = `
      margin-top:8px; width:100%; padding:8px; cursor:pointer; font-family:inherit;
      background:#555; border:2px solid; border-color:#777 #333 #333 #777;
      color:#EEE; font-size:13px; font-weight:bold;`;
    girar.addEventListener('click', () => { this._dorso = !this._dorso; this._pintarPreview(); });
    lado.appendChild(girar);
    cuerpo.appendChild(lado);

    const derecha = document.createElement('div');
    derecha.style.cssText = 'flex:1 1 380px; min-width:0; display:flex; flex-direction:column; gap:10px;';

    // filtros por categoría
    const filtros = document.createElement('div');
    filtros.style.cssText = 'display:flex; gap:6px; flex-wrap:wrap;';
    this._filtros = [];
    for (const c of [{ id: 'todas', nombre: 'Todas' }, ...CATEGORIAS]) {
      const b = document.createElement('button');
      b.textContent = c.nombre;
      b.dataset.cat = c.id;
      b.style.cssText = `
        padding:6px 10px; cursor:pointer; font-family:inherit; font-size:12px;
        background:#3A3A3A; border:2px solid #555; color:#DDD;`;
      b.addEventListener('click', () => {
        this._cat = c.id;
        this._pintarRejilla();
        this._rejillaPintada = true;
      });
      filtros.appendChild(b);
      this._filtros.push(b);
    }
    derecha.appendChild(filtros);

    this._grid = document.createElement('div');
    this._grid.style.cssText = `
      display:grid; grid-template-columns:repeat(auto-fill,minmax(84px,1fr));
      gap:8px; overflow-y:auto; max-height:52vh; padding:4px;`;
    derecha.appendChild(this._grid);
    cuerpo.appendChild(derecha);
    panel.appendChild(cuerpo);

    const cerrar = document.createElement('button');
    cerrar.textContent = 'Cerrar';
    cerrar.style.cssText = `
      padding:10px; cursor:pointer; font-family:inherit; background:#555;
      border:2px solid; border-color:#777 #333 #333 #777; color:#EEE;
      font-size:14px; font-weight:bold;`;
    cerrar.addEventListener('click', () => this.hide());
    panel.appendChild(cerrar);

    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);

    /* La rejilla NO se pinta aquí.

       Pintar los cuarenta retratos cuesta 211 ms de hilo principal —medido en
       un M3— y se estaban gastando al arrancar el juego, para un menú que está
       cerrado y que puede que no se abra nunca. Se pinta la primera vez que se
       abre. */
    this._rejillaPintada = false;
    this._pintarPreview();
  }

  _pintarRejilla() {
    const lista = this._cat === 'todas' ? SKINS : porCategoria(this._cat);
    for (const b of this._filtros) {
      const activa = b.dataset.cat === this._cat;
      b.style.borderColor = activa ? '#FFD700' : '#555';
      b.style.color = activa ? '#FFD700' : '#DDD';
    }
    this._grid.textContent = '';
    for (const skin of lista) {
      const card = document.createElement('button');
      card.dataset.skinId = skin.id;
      card.title = skin.nombre;
      card.style.cssText = `
        padding:6px 4px; background:#3A3A3A; cursor:pointer; font-family:inherit;
        border:2px solid ${skin.id === this.currentSkin.id ? '#FFD700' : '#555'};
        display:flex; flex-direction:column; align-items:center; gap:4px;`;
      const img = retrato(skin, 3);
      img.style.cssText = 'image-rendering:pixelated; width:48px; height:96px;';
      card.appendChild(img);
      const n = document.createElement('span');
      n.textContent = skin.nombre;
      n.style.cssText = 'font-size:10px;color:#EEE;text-align:center;line-height:1.2;';
      card.appendChild(n);
      card.addEventListener('click', () => this._elegir(skin));
      this._grid.appendChild(card);
    }
  }

  _pintarPreview() {
    this._preview.textContent = '';
    const img = retrato(this.currentSkin, 6, this._dorso);
    img.style.cssText = 'image-rendering:pixelated; width:96px; height:192px;';
    this._preview.appendChild(img);
    this._nombre.textContent = this.currentSkin.nombre;
  }

  _elegir(skin) {
    this.currentSkin = skin;
    try { localStorage.setItem(CLAVE, skin.id); } catch { /* modo privado */ }
    for (const card of this._grid.children)
      card.style.borderColor = card.dataset.skinId === skin.id ? '#FFD700' : '#555';
    this._pintarPreview();
    if (this.onSkinChanged) this.onSkinChanged(skin);
  }

  show() {
    if (!this._rejillaPintada) { this._pintarRejilla(); this._rejillaPintada = true; }
    this._overlay.style.display = 'flex';
    this.isOpen = true;
  }
  hide() { this._overlay.style.display = 'none'; this.isOpen = false; }
}
